export interface ProductData {
  title: string;
  price?: number;
  image?: string;
  currency?: string;
}

export class SimpleURLParser {
  static async parseURL(url: string): Promise<ProductData> {
    try {
      const urlBasedData = this.parseFromURL(url);
      
      // Try simple scraping endpoint if available
      try {
        const response = await fetch('/api/scrape', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url })
        });

        if (response.ok) {
          const scrapedData = await response.json();
          return { ...urlBasedData, ...scrapedData };
        }
      } catch (e) {
        // Fallback to URL parsing
      }

      return urlBasedData;
    } catch (error) {
      return this.parseFromURL(url);
    }
  }

  private static parseFromURL(url: string): ProductData {
    const urlObj = new URL(url);
    const currency = this.detectCurrencyFromUrl(url);
    
    if (urlObj.hostname.includes('amazon.')) {
      return { ...this.parseAmazonURL(url), currency };
    }
    
    if (urlObj.hostname.includes('target.com')) {
      return { ...this.parseTargetURL(url), currency };
    }
    
    if (urlObj.hostname.includes('noon.com')) {
      return { ...this.parseNoonURL(url), currency };
    }

    // Generic fallback
    const pathSegments = urlObj.pathname.split('/').filter(Boolean);
    const title = pathSegments[pathSegments.length - 1]
      ?.replace(/[-_]/g, ' ')
      ?.replace(/\.(html|htm|php)$/, '') || 'Product';

    return {
      title: this.formatTitle(title),
      price: undefined,
      image: undefined,
      currency
    };
  }

  private static parseAmazonURL(url: string): ProductData {
    // Extract product ID from Amazon URL
    const dpMatch = url.match(/\/dp\/([A-Z0-9]+)/);
    const gpMatch = url.match(/\/gp\/product\/([A-Z0-9]+)/);
    const productId = dpMatch?.[1] || gpMatch?.[1];
    
    // Extract title from URL path - improved regex for better parsing
    const pathMatch = url.match(/\/dp\/[A-Z0-9]+\/([^/?&]+)/);
    let title = 'Amazon Product';
    
    if (pathMatch) {
      title = pathMatch[1]
        .replace(/[-_]/g, ' ')
        .replace(/\+/g, ' ')
        .split('?')[0]
        .split('&')[0]
        .split('/')[0] // Remove additional path segments
        .replace(/ref=sr.*$/i, '') // Remove ref=sr parameters
        .trim();
    }

    // Try to construct image URL for Amazon products
    let imageUrl = undefined;
    if (productId) {
      // Try different Amazon image URL patterns - use simpler format first
      const imagePatterns = [
        `https://m.media-amazon.com/images/I/${productId}.jpg`,
        `https://m.media-amazon.com/images/I/${productId}._AC_SL1500_.jpg`,
        `https://m.media-amazon.com/images/I/${productId}._AC_SX679_.jpg`,
        `https://images-na.ssl-images-amazon.com/images/I/${productId}.jpg`,
        `https://images-na.ssl-images-amazon.com/images/I/${productId}._AC_SL1500_.jpg`,
        `https://images.amazon.com/images/I/${productId}.jpg`
      ];
      
      // Use the first pattern (simplest format)
      imageUrl = imagePatterns[0];
    }

    return {
      title: this.formatTitle(title),
      price: undefined,
      image: imageUrl,
      currency: this.detectCurrencyFromUrl(url)
    };
  }

  private static parseTargetURL(url: string): ProductData {
    const match = url.match(/\/p\/([^/]+)/);
    if (match) {
      return {
        title: this.formatTitle(match[1].replace(/[-_]/g, ' ')),
        price: undefined,
        image: undefined
      };
    }
    return { title: 'Target Product' };
  }

  private static parseNoonURL(url: string): ProductData {
    // Extract product ID from Noon URL (for future use)
    // const productIdMatch = url.match(/\/N(\d+)V\//);
    // const productId = productIdMatch?.[1];
    
    // Extract title from URL path
    const pathMatch = url.match(/\/uae-en\/([^/]+)\/N\d+V/);
    let title = 'Noon Product';
    
    if (pathMatch) {
      title = pathMatch[1]
        .replace(/[-_]/g, ' ')
        .replace(/\+/g, ' ')
        .split('?')[0]
        .split('&')[0]
        .trim();
    }

    return {
      title: this.formatTitle(title),
      price: undefined,
      image: undefined,
      currency: this.detectCurrencyFromUrl(url)
    };
  }

  private static formatTitle(title: string): string {
    return title
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .substring(0, 100);
  }

  /**
   * Detect currency from URL/domain
   */
  private static detectCurrencyFromUrl(url: string): string {
    const urlLower = url.toLowerCase();
    
    // UAE/Arab Emirates
    if (urlLower.includes('uae.') || urlLower.includes('.ae/') || urlLower.includes('microless') || urlLower.includes('amazon.ae') || urlLower.includes('noon.com')) {
      return 'AED';
    }
    
    // UK
    if (urlLower.includes('.co.uk/') || urlLower.includes('.uk/')) {
      return 'GBP';
    }
    
    // Canada
    if (urlLower.includes('.ca/')) {
      return 'CAD';
    }
    
    // Australia
    if (urlLower.includes('.com.au/') || urlLower.includes('.au/')) {
      return 'AUD';
    }
    
    // Europe
    if (urlLower.includes('.de/') || urlLower.includes('.fr/') || urlLower.includes('.it/') || 
        urlLower.includes('.es/') || urlLower.includes('.nl/')) {
      return 'EUR';
    }
    
    // Japan
    if (urlLower.includes('.jp/')) {
      return 'JPY';
    }
    
    // India
    if (urlLower.includes('.in/')) {
      return 'INR';
    }
    
    // Singapore
    if (urlLower.includes('.sg/')) {
      return 'SGD';
    }
    
    // Default to USD
    return 'USD';
  }
}
